import { createAction } from 'redux-actions';
import { callApi, getEndpoint } from '../../../utils/api';
import { getLastFour } from '../../../utils/banking/bank-utils';
import { coalesce } from '../../../utils/utils';
import { RETREIVE_FAILURE_HISTORY } from '../eVerification/eVerification.module';
import settings from '../../../settings/settings';

const ADD_LINKED_BANK = 'ADD_LINKED_BANK';
const SIMULATE_LINKED_BANK = 'SIMULATE_LINKED_BANK';
const SAVE_LINKED_BANK = 'SAVE_LINKED_BANK';
const INITIALIZE_BANK_CONNECTION = 'INITIALIZE_BANK_CONNECTION';
const BANK_AUTH_STATUS = 'BANK_AUTH_STATUS';
const RETRIEVING_BANK_ACCOUNTS = 'RETRIEVING_BANK_ACCOUNTS';
const SUCCESSFUL = 'SUCCESSFUL';
const IN_PROGRESS = 'IN_PROGRESS';
const LINK_BANK_ATTEMPT = 'LINK_BANK_ATTEMPT';
const RESET_LINK_BANK = 'RESET_LINK_BANK';
const LINK_BANK_FAILURE = 'LINK_BANK_FAILURE';
const LOG_SYNC_EVENT = 'LOG_SYNC_EVENT';
const LOG_LOAD_EVENT = 'LOG_LOAD_EVENT';
const LOG_OPEN_EVENT = 'LOG_OPEN_EVENT';
const LOG_CLOSE_EVENT = 'LOG_CLOSE_EVENT';
const LOG_ADD_EVENT = 'LOG_ADD_EVENT';
const LOG_START_EVENT = 'LOG_START_EVENT';
const LOG_ISSUE = 'LOG_ISSUE';
const ANY_STOP_TRANSFER = 'ANY_STOP_TRANSFER';
const COLLEGE_CLIENT_ONLY = 'COLLEGE_CLIENT_ONLY';
const MD529_DECOMMISSION_ZERO_STATE = 'MD529_DECOMMISSION_ZERO_STATE';
const NON_ELIGIBLE_AGREEMENTS = 'NON_ELIGIBLE_AGREEMENTS';
const TRAC_OR_RPS_ONLY = 'TRAC_OR_RPS_ONLY';
const CUSTODIAN_ONLY = 'CUSTODIAN_ONLY';

/** Checks whether the bank shown in Quovo is already present in the existingBanks and if it is already present it modifies disable flag to true and makes
 * radio button in Quovo disabled else remains false and is enabled
 * @param {Array} linkedBankList Which contains bank details like accountNumber, routingNumber  and so on..
 * @param {object}  existingBanks Which also contains list of Banks and its details
 * @return{Array} linkedBankList Which updates the disable flag according to the condition
 */
export const disableAlreadyExistingBanks = (linkedBankList, existingBanks) => {
  linkedBankList.forEach(
    (linkedBank) =>
      (linkedBank.disable = existingBanks.existingBanks.some(
        (existingBank) =>
          getLastFour(linkedBank.accountNumber) ===
            getLastFour(existingBank.accountNumber) &&
          linkedBank.routingNumber === existingBank.routingNumber &&
          linkedBank.accountType.toUpperCase() ===
            existingBank.bankAccountType.toUpperCase()
      ))
  );
  return linkedBankList;
};

const initialLinkBankState = {
  authId: null,
  attempts: 0,
  historyLoading: false,
  historyLoaded: false,
  failureHistory: null,
  addBankLoading: false,
  addBankStatus: 'INIT',
  addBankError: false,
  saveBankLoading: false,
  saveBankStatus: 'INIT',
  vendorLoading: false,
  vendorLoaded: false,
  vendorSetupFailed: false,
  banksLoaded: false,
  banksLoading: false,
  bankAccountError: false,
  environment: null,
  error: null,
  hasFailed: false,
  hasErrorType: false,
  errorType: null,
  bankAccounts: [],
  loggingBankFailure: false,
  logResponse: null,
  loggingFailed: false,
  keepPolling: null,
  publicKey: null,
  retrievalStatus: 'INIT',
  statusLoading: false,
  statusLoaded: false,
  statusFailed: false,
  synced: false,
  vendorToken: null,
};

export default function linkedBanks(
  state = initialLinkBankState,
  { payload, type }
) {
  switch (type) {
    case `${INITIALIZE_BANK_CONNECTION}_PENDING`:
      return {
        ...state,
        vendorLoading: true,
      };
    case `${INITIALIZE_BANK_CONNECTION}_FULFILLED`:
      return {
        ...initialLinkBankState,
        attempts: state.attempts,
        vendorLoading: false,
        vendorLoaded: true,
        authId: payload.body.authenticationId,
        environment: payload.body.environment,
        publicKey: payload.body.publicKey,
        vendorToken: payload.body.userToken,
      };
    case `${INITIALIZE_BANK_CONNECTION}_REJECTED`:
      return {
        ...state,
        vendorLoading: false,
        hasFailed: true,
        vendorSetupFailed: true,
        error: '0000',
      };
    case `${BANK_AUTH_STATUS}_PENDING`:
      return {
        ...state,
        statusLoading: true,
      };
    case `${BANK_AUTH_STATUS}_FULFILLED`:
      return {
        ...state,
        retrievalStatus: payload.body.status,
        statusLoading: false,
        statusLoaded: true,
        hasFailed: payload.body.status === 'FAILED',
        statusFailed: payload.body.status === 'FAILED',
        error: payload.body.status === 'FAILED' ? '0001' : null,
        keepPolling: payload.body.status === IN_PROGRESS ? true : false,
        synced: payload.body.status === SUCCESSFUL ? true : false,
      };
    case `${BANK_AUTH_STATUS}_REJECTED`:
      return {
        ...state,
        statusLoading: false,
        hasFailed: true,
        statusFailed: true,
        error: '0001',
      };
    case `${RETRIEVING_BANK_ACCOUNTS}_PENDING`:
      return {
        ...state,
        addBankStatus: 'INIT',
        bankAccounts: [],
        banksLoading: true,
      };
    case `${RETRIEVING_BANK_ACCOUNTS}_FULFILLED`:
      return {
        ...state,
        authId: payload.authId,
        attempts: state.attempts + 1,
        bankAccounts: payload.updatedLinkedBankList,
        banksLoading: false,
        banksLoaded: true,
      };

    case `${RETRIEVING_BANK_ACCOUNTS}_REJECTED`:
      return {
        ...state,
        attempts: state.attempts + 1,
        banksLoading: false,
        hasFailed: true,
        bankAccountError: true,
        error: '0002',
      };
    case `${LINK_BANK_FAILURE}_PENDING`:
      return {
        ...state,
        loggingBankFailure: true,
      };
    case `${LINK_BANK_FAILURE}_FULFILLED`:
      return {
        ...state,
        loggingBankFailure: false,
        logResponse: payload.body,
        loggingFailed: false,
      };
    case `${ADD_LINKED_BANK}_PENDING`:
      return {
        ...state,
        addBankLoading: true,
      };
    case `${ADD_LINKED_BANK}_FULFILLED`:
      return {
        ...state,
        addBankStatus: payload.status === 200 ? 'SUCCESSFUL' : 'FAILED',
        bankAdditionSuccess: payload.body.bankAdditionSuccess,
        bankAdditionPartialSuccess: payload.body.bankAdditionPartialSuccess,
        isBankDuplicate:
          payload.body?.existingBankStatus?.bankVerificationStatus
            ?.enumerationID === 40
            ? true
            : false,
        addBankLoading: false,
      };
    case `${ADD_LINKED_BANK}_REJECTED`:
      return {
        ...state,
        addBankLoading: false,
        addBankStatus: 'FAILED',
        hasFailed: true,
        addBankError: true,
        error: '0003',
      };
    case `${SIMULATE_LINKED_BANK}_PENDING`:
      return {
        ...state,
        addBankLoading: true,
      };
    case `${SIMULATE_LINKED_BANK}_FULFILLED`:
      return {
        ...state,
        addBankStatus: payload.status === 200 ? 'SUCCESSFUL' : 'FAILED',
        bankAdditionSuccess: payload.body.bankAdditionSuccess,
        bankAdditionPartialSuccess: payload.body.bankAdditionPartialSuccess,
        isBankDuplicate:
          payload.body?.existingBankStatus?.bankVerificationStatus
            ?.enumerationID === 40
            ? true
            : false,
        addBankLoading: false,
      };
    case `${SIMULATE_LINKED_BANK}_REJECTED`:
      return {
        ...state,
        addBankLoading: false,
        addBankStatus: 'FAILED',
        hasFailed: true,
        addBankError: true,
        error: '0003',
      };
    case `${SAVE_LINKED_BANK}_PENDING`:
      return {
        ...state,
        saveBankLoading: true,
      };
    case `${SAVE_LINKED_BANK}_FULFILLED`:
      return {
        ...state,
        addBankStatus: payload.status === 200 ? 'SUCCESSFUL' : 'FAILED',
        saveBankLoading: false,
      };
    case `${SAVE_LINKED_BANK}_REJECTED`:
      return {
        ...state,
        saveBankLoading: false,
        saveBankStatus: 'FAILED',
      };
    case `${LINK_BANK_FAILURE}_REJECTED`:
      return {
        ...state,
        loggingBankFailure: false,
        loggingFailed: true,
      };
    case `${LINK_BANK_ATTEMPT}_FULFILLED`:
      return {
        ...state,
        attempts: state.attempts,
      };
    case `${RETREIVE_FAILURE_HISTORY}_PENDING`:
      return {
        ...state,
        historyLoading: true,
      };
    case `${RETREIVE_FAILURE_HISTORY}_FULFILLED`: {
      const { plaidFailureHistory: { bankFailureEventList = [] } = {} } =
        payload.body || {};
      return {
        ...state,
        historyLoaded: true,
        historyLoading: false,
        attempts: state.attempts + (bankFailureEventList.length || 0),
      };
    }
    case `${RETREIVE_FAILURE_HISTORY}_REJECTED`:
      return {
        ...state,
        historyLoaded: true,
        historyFailed: true,
        historyLoading: false,
      };
    case `${RESET_LINK_BANK}_FULFILLED`:
      return {
        authId: null,
        vendorLoading: false,
        vendorLoaded: false,
        vendorSetupFailed: false,
        banksLoaded: false,
        banksLoading: false,
        bankAccountError: false,
        environment: null,
        error: null,
        hasFailed: false,
        bankAccounts: [],
        loggingBankFailure: false,
        logResponse: null,
        loggingFailed: false,
        keepPolling: null,
        publicKey: null,
        retrievalStatus: 'INIT',
        statusLoading: false,
        statusLoaded: false,
        statusFailed: false,
        synced: false,
        vendorToken: null,
      };
    case MD529_DECOMMISSION_ZERO_STATE: {
      return {
        hasErrorType: true,
        errorType: MD529_DECOMMISSION_ZERO_STATE,
      };
    }
    case NON_ELIGIBLE_AGREEMENTS: {
      return {
        hasErrorType: true,
        errorType: NON_ELIGIBLE_AGREEMENTS,
      };
    }
    case ANY_STOP_TRANSFER: {
      return {
        hasErrorType: true,
        errorType: ANY_STOP_TRANSFER,
      };
    }
    case COLLEGE_CLIENT_ONLY: {
      return {
        hasErrorType: true,
        errorType: COLLEGE_CLIENT_ONLY,
      };
    }
    case TRAC_OR_RPS_ONLY: {
      return {
        hasErrorType: true,
        errorType: TRAC_OR_RPS_ONLY,
      };
    }
    case CUSTODIAN_ONLY: {
      return {
        hasErrorType: true,
        errorType: CUSTODIAN_ONLY,
      };
    }
    default:
      return state;
  }
}

export const initializeBankConnection = () => {
  return (dispatch) => {
    const payload = callApi(getEndpoint('initBank'));
    const action = createAction(INITIALIZE_BANK_CONNECTION)(payload);
    dispatch(action);
  };
};

export const determineAuthStatus = (authenticationId) => {
  return (dispatch) => {
    const options = {
      method: 'POST',
      authenticationId,
    };
    const data = {
      authenticationId: authenticationId,
    };
    const payload = callApi(
      getEndpoint('bankAuth'),
      options,
      JSON.stringify(data)
    );
    const action = createAction(BANK_AUTH_STATUS)(payload);
    dispatch(action);
  };
};

export const getBankAccounts = (data) => {
  return (dispatch, getState) => {
    const options = {
      method: 'POST',
    };
    const pendingAction = createAction(`${RETRIEVING_BANK_ACCOUNTS}_PENDING`)(
      null
    );
    dispatch(pendingAction);
    callApi(
      getEndpoint('plaidBankingOnSuccess'),
      options,
      JSON.stringify(data)
    ).then((response) => {
      const linkedBankList = response.body;
      const { existingBanks } = getState();
      const updatedLinkedBankList = disableAlreadyExistingBanks(
        linkedBankList,
        existingBanks
      );
      const action = createAction(`${RETRIEVING_BANK_ACCOUNTS}_FULFILLED`)({
        updatedLinkedBankList: updatedLinkedBankList,
        authId: data.authenticationId,
      });
      dispatch(action);
    });
  };
};

export const setErrorType = (errorType) => {
  return (dispatch) => {
    if (errorType === MD529_DECOMMISSION_ZERO_STATE) {
      const action = createAction(MD529_DECOMMISSION_ZERO_STATE)(true);
      dispatch(action);
    } else if (errorType === NON_ELIGIBLE_AGREEMENTS) {
      const action = createAction(NON_ELIGIBLE_AGREEMENTS)(true);
      dispatch(action);
    } else if (errorType === ANY_STOP_TRANSFER) {
      const action = createAction(ANY_STOP_TRANSFER)(true);
      dispatch(action);
    } else if (errorType === COLLEGE_CLIENT_ONLY) {
      const action = createAction(COLLEGE_CLIENT_ONLY)(true);
      dispatch(action);
    } else if (errorType === TRAC_OR_RPS_ONLY) {
      const action = createAction(TRAC_OR_RPS_ONLY)(true);
      dispatch(action);
    } else if (errorType === CUSTODIAN_ONLY) {
      const action = createAction(CUSTODIAN_ONLY)(true);
      dispatch(action);
    }
  };
};

export const incrementLinkAttempts = () => {
  return (dispatch) => {
    const action = createAction(`${LINK_BANK_ATTEMPT}_FULFILLED`)(null);
    dispatch(action);
  };
};

export const logLinkFailure = (failure) => {
  const options = {
    method: 'POST',
  };
  return (dispatch) => {
    const action = createAction(LINK_BANK_FAILURE)(
      callApi(getEndpoint('linkBankFailure'), options, JSON.stringify(failure))
    );
    dispatch(action);
  };
};

export const addLinkedBank = (data) => {
  const options = {
    method: 'POST',
  };
  const payload = data;
  return (dispatch) => {
    if (settings.isStage) {
      const action = createAction(ADD_LINKED_BANK)(
        callApi(
          getEndpoint('addBankPlaidLowerEnv'),
          options,
          JSON.stringify(payload)
        )
      );
      dispatch(action);
    } else {
      const action = createAction(ADD_LINKED_BANK)(
        callApi(getEndpoint('addBankPlaid'), options, JSON.stringify(payload))
      );
      dispatch(action);
    }
  };
};

export const simulateLinkedBank = (token) => {
  const options = {
    method: 'POST',
  };
  const payload = {
    token,
  };
  return (dispatch) => {
    const action = createAction(SIMULATE_LINKED_BANK)(
      callApi(
        getEndpoint('simulateLinkedBank'),
        options,
        JSON.stringify(payload)
      )
    );
    dispatch(action);
  };
};

export const saveLinkedBank = (token) => {
  const options = {
    method: 'POST',
  };
  const payload = {
    token,
  };
  return (dispatch) => {
    const action = createAction(SAVE_LINKED_BANK)(
      callApi(
        getEndpoint('simulateLinkedBank'),
        options,
        JSON.stringify(payload)
      )
    );
    dispatch(action);
  };
};

export const logOnLoad = (authId) => {
  const options = {
    method: 'POST',
  };
  const body = {
    authenticationId: authId,
  };
  return (dispatch) => {
    const action = createAction(LOG_LOAD_EVENT)(
      callApi(getEndpoint('onLoad'), options, JSON.stringify(body))
    );
    dispatch(action);
  };
};

export const logOnOpen = (authId) => {
  const options = {
    method: 'POST',
  };
  const body = {
    authenticationId: authId,
  };
  return (dispatch) => {
    const action = createAction(LOG_OPEN_EVENT)(
      callApi(getEndpoint('onOpen'), options, JSON.stringify(body))
    );
    dispatch(action);
  };
};

export const logOnClose = (authId) => {
  const options = {
    method: 'POST',
  };
  const body = {
    authenticationId: authId,
  };
  return (dispatch) => {
    const action = createAction(LOG_CLOSE_EVENT)(
      callApi(getEndpoint('onClose'), options, JSON.stringify(body))
    );
    dispatch(action);
  };
};

export const logIssue = (authId, issuesArray) => {
  const options = {
    method: 'POST',
  };
  const body = {
    authenticationId: authId,
    accountIssues: issuesArray,
  };
  return (dispatch) => {
    const action = createAction(LOG_ISSUE)(
      callApi(getEndpoint('issues'), options, JSON.stringify(body))
    );
    dispatch(action);
  };
};

export const logOnAdd = (authId, syncError, event) => {
  const options = {
    method: 'POST',
  };
  const body = {
    authenticationId: coalesce(authId, ''),
    error: {
      timeStamp: coalesce(event.timeStamp, Date.now()),
    },
    event: {
      timeStamp: coalesce(event.timeStamp, Date.now()),
    },
  };

  if (syncError) {
    body.error.syncError = {
      status: coalesce(syncError.status, 99999999999),
      id: coalesce(syncError.id, 99999999999),
      message: coalesce(syncError.message, ''),
    };
    body.syncError = {
      status: coalesce(syncError.status, 99999999),
      id: coalesce(syncError.id, 99999999),
      message: coalesce(syncError.message, ''),
    };
  }

  if (event && event.connection) {
    body.event.connection = {
      id: coalesce(event.connection.id, 99999999),
      institution: coalesce(event.connection.institution, 99999999),
      user: coalesce(event.connection.user, 99999999),
    };
  }

  return (dispatch) => {
    const action = createAction(LOG_ADD_EVENT)(
      callApi(getEndpoint('onAdd'), options, JSON.stringify(body))
    );
    dispatch(action);
  };
};

export const logOnSync = (authId, syncError, event) => {
  const options = {
    method: 'POST',
  };

  const body = {
    authenticationId: coalesce(authId, ''),
    error: {
      timeStamp: coalesce(event.timeStamp, Date.now()),
    },
    event: {
      timeStamp: coalesce(event.timeStamp, Date.now()),
    },
  };

  if (syncError) {
    body.error.syncError = {
      status: coalesce(syncError.status, 99999999),
      id: coalesce(syncError.id, 99999999),
      message: coalesce(syncError.message, ''),
    };
    body.syncError = {
      status: coalesce(syncError.status, 99999999),
      id: coalesce(syncError.id, 99999999),
      message: coalesce(syncError.message, ''),
    };
  }

  if (event && event.connection) {
    body.event.connection = {
      id: coalesce(event.connection.id, 99999999),
      institution: coalesce(event.connection.institution, 99999999),
      user: coalesce(event.connection.user, 99999999),
    };
  }
  if (event.sync) {
    body.event.sync = {
      status: coalesce(event.sync.status, 'NA'),
      connection: coalesce(event.sync.connectionId, 99999999),
      authenticated: coalesce(event.sync.authenticated, false),
    };
  }

  return (dispatch) => {
    const action = createAction(LOG_SYNC_EVENT)(
      callApi(getEndpoint('onSync'), options, JSON.stringify(body))
    );
    dispatch(action);
  };
};

export const getPlaidConfig = () => {
  const options = {
    method: 'POST',
  };
  const body = {};
  return (dispatch) => {
    const action = createAction(LOG_START_EVENT)(
      callApi(getEndpoint('plaidBankingConfig'), options, JSON.stringify(body))
    );
    dispatch(action);
  };
};

export const resetLink = () => {
  return (dispatch) => {
    const action = createAction(`${RESET_LINK_BANK}_FULFILLED`)(null);
    dispatch(action);
  };
};
